<template>
    <div>
        <!-- Barre nom / version / scope / actions -->
        <div class="d-flex flex-wrap align-items-center" style="gap:4px">
            <b-input v-model="name" placeholder="Nom" size="sm" style="max-width:180px" />
            <b-input v-model="version" placeholder="Version" size="sm" style="max-width:120px" />
            <b-select v-if="scopeOptions.length > 1" v-model="scope" size="sm" style="max-width:140px"
                :options="scopeOptions" />
            <b-button size="sm" variant="outline-success" :disabled="!name || !canSaveCurrentScope" @click="save">
                <i class="fas fa-save"></i> Enregistrer
            </b-button>
            <b-button v-if="canPersonal || canViewClient" size="sm" variant="outline-secondary" @click="openLoadModal">
                <i class="fas fa-folder-open"></i> Charger
            </b-button>
            <b-button v-if="name" size="sm" variant="outline-secondary" @click="reset" title="Nouveau">
                <i class="fas fa-plus"></i>
            </b-button>
            <b-button v-if="currentUid && currentIsPublic && canSetDefault" size="sm"
                :variant="currentIsDefault ? 'warning' : 'outline-secondary'"
                @click="toggleDefaultCurrent()"
                :title="currentIsDefault ? 'Retirer comme configuration par défaut' : 'Définir comme configuration par défaut'">
                <i :class="currentIsDefault ? 'fas fa-star' : 'far fa-star'"></i>
            </b-button>
        </div>
        <small v-if="saveMsg" :class="saveError ? 'text-danger' : 'text-success'" class="d-block mt-1">{{ saveMsg }}</small>

        <!-- Modal charger / gérer -->
        <b-modal :id="modalId" title="Configurations" :hide-footer="true" size="lg">
            <!-- En-tête du modal : filtres -->
            <div class="d-flex align-items-center mb-3" style="gap:8px">
                <b-input v-model="search" placeholder="Rechercher…" size="sm" style="max-width:200px" />
                <b-button size="sm" variant="outline-secondary" @click="fetchConfigs">
                    <i class="fas fa-sync-alt"></i>
                </b-button>
                <span class="text-muted small ml-auto">{{ filteredConfigs.length }} configuration(s)</span>
            </div>

            <div v-if="loadingList" class="text-muted text-center py-3">Chargement…</div>
            <div v-else-if="filteredConfigs.length === 0" class="text-muted text-center py-3">
                Aucune configuration enregistrée.
            </div>

            <!-- Liste des configs (groupées par nom) -->
            <div v-for="cfg in filteredConfigs" :key="cfg.uid" class="mb-2 border rounded">
                <!-- En-tête de la config -->
                <div class="d-flex align-items-center px-3 py-2" style="background:#f8f9fa;border-radius:4px 4px 0 0">
                    <!-- Mode renommage -->
                    <template v-if="renamingUid === cfg.uid">
                        <b-input v-model="renameValue" size="sm" style="max-width:200px"
                            @keydown.enter.native="confirmRename(cfg)"
                            @keydown.esc.native="cancelRename" />
                        <b-button size="sm" variant="success" class="ml-1" @click="confirmRename(cfg)">
                            <i class="fas fa-check"></i>
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" class="ml-1" @click="cancelRename">
                            <i class="fas fa-times"></i>
                        </b-button>
                    </template>
                    <template v-else>
                        <span class="font-weight-bold">{{ cfg.name }}</span>
                        <small class="text-muted ml-2">
                            <template v-if="cfg.client_user_uid === _currentUserUid">
                                ({{ cfg.shared === 'readonly' ? 'personnelle, partagée' : 'personnelle' }})
                            </template>
                            <template v-else-if="cfg.client_user_uid">
                                ({{ cfg.creator_name || 'Utilisateur inconnu' }})
                            </template>
                            <template v-else>
                                (publique)
                            </template>
                        </small>
                    </template>

                    <div class="ml-auto d-flex" style="gap:4px">
                        <!-- Définir par défaut (configs publiques uniquement) -->
                        <b-button v-if="!cfg.client_user_uid && canSetDefault" size="sm"
                            :variant="cfg.is_default ? 'warning' : 'outline-secondary'"
                            @click="toggleDefault(cfg)"
                            :title="cfg.is_default ? 'Retirer comme configuration par défaut' : 'Définir comme configuration par défaut'">
                            <i :class="cfg.is_default ? 'fas fa-star' : 'far fa-star'"></i>
                        </b-button>
                        <!-- Charger la version active -->
                        <b-button size="sm" variant="outline-primary" @click="applyConfig(cfg)" title="Charger version active">
                            <i class="fas fa-download"></i> Charger
                        </b-button>
                        <!-- Versions -->
                        <b-button size="sm" :variant="openVersions[cfg.uid] ? 'secondary' : 'outline-secondary'"
                            @click="toggleVersions(cfg)" title="Versions">
                            <i class="fas fa-history"></i>
                        </b-button>
                        <!-- Renommer -->
                        <b-button v-if="cfg.can_edit" size="sm" variant="outline-secondary"
                            @click="startRename(cfg)" title="Renommer">
                            <i class="fas fa-pencil-alt"></i>
                        </b-button>
                        <!-- Supprimer toutes les versions -->
                        <b-button v-if="cfg.can_edit" size="sm" variant="outline-danger"
                            @click="deleteConfig(cfg)" title="Supprimer toutes les versions">
                            <i class="fas fa-trash"></i>
                        </b-button>
                    </div>
                </div>

                <!-- Liste des versions -->
                <div v-if="openVersions[cfg.uid]" class="px-3 py-2">
                    <div v-if="!versionsData[cfg.uid]" class="text-muted small">Chargement…</div>
                    <div v-else-if="versionsData[cfg.uid].length === 0" class="text-muted small">Aucune version.</div>
                    <div v-else v-for="v in versionsData[cfg.uid]" :key="v.uid"
                        class="d-flex align-items-center py-1" style="border-top:1px solid #eee;gap:8px">
                        <b-badge :variant="v.production ? 'success' : 'secondary'" style="min-width:36px">
                            {{ v.version }}
                        </b-badge>
                        <span class="text-muted small">{{ formatDate(v.date) }}</span>
                        <b-badge v-if="v.production" variant="success">active</b-badge>
                        <div class="ml-auto d-flex" style="gap:4px">
                            <b-button size="sm" variant="outline-primary" @click="applyVersion(v, cfg)">
                                <i class="fas fa-download"></i>
                            </b-button>
                            <b-button v-if="v.can_edit && !v.production" size="sm" variant="outline-success"
                                @click="restoreVersion(v, cfg)" title="Mettre en production">
                                <i class="fas fa-undo"></i>
                            </b-button>
                            <b-button v-if="v.can_edit" size="sm" variant="outline-danger"
                                @click="deleteVersion(v, cfg)" title="Supprimer cette version">
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </div>
                    </div>
                </div>

                <small v-if="configError[cfg.uid]" class="text-danger px-3 d-block pb-1">{{ configError[cfg.uid] }}</small>
            </div>
        </b-modal>
    </div>
</template>

<script>
import configService from '../services/config.js';
import store from '../store';

export default {
    name: 'ConfigSaveLoad',

    props: {
        /** Données à sauvegarder (objet JSON) */
        value:   { type: Object, required: true },
        /** Type de config (ex: 'chart') — discriminant dans la table */
        type:    { type: String, required: true },
        /** Contexte optionnel pour isoler les configs par table/usage (ex: 'vente', 'commandes') */
        context: { type: String, default: null },
        /** Scopes autorisés parmi 'personal', 'shared', 'client'. null = tous (selon les droits). */
        allowedScopes: { type: Array, default: null },
        /** Fonction async optionnelle — appelée au moment de sauvegarder pour obtenir la valeur (remplace :value) */
        getValueFn: { type: Function, default: null }
    },

    data() {
        return {
            uid:     Math.random().toString(36).slice(2),
            // champs d'en-tête
            name:    '',
            version: '',
            scope:   'client',  // ajusté au mounted selon les droits
            saveMsg:   '',
            saveError: false,
            // modal liste
            loadingList:  false,
            configList:   [],
            search:       '',
            openVersions: {},   // { [cfg.uid]: bool }
            versionsData: {},   // { [cfg.uid]: Array|null }
            configError:  {},   // { [cfg.uid]: string }
            // renommage inline
            renamingUid:  null,
            renameValue:  '',
            // config courante (chargée)
            currentUid:       null,
            currentIsDefault: false,
            currentIsPublic:  false
        };
    },

    computed: {
        modalId() {
            return 'modal-config-saveload-' + this.uid;
        },

        // ── Droits ───────────────────────────────────────────────────────────
        // Miroir exact de la logique backend (_clientRight / _canPersonal / _canEditClient)

        _rules() {
            return store.state.user ? store.state.user.rules : undefined;
        },
        _currentUserUid() {
            return store.state.user ? store.state.user.uid : null;
        },
        // rules === null → admin (tout autorisé)
        _isAdmin() {
            return this._rules === null;
        },
        // Droit effectif pour les configs client de ce type : 'edit' | 'view' | false
        _clientRight() {
            if (this._isAdmin) return 'edit';
            const rules = this._rules || {};
            if (rules.config_type && rules.config_type[this.type] !== undefined) {
                const r = rules.config_type[this.type];
                if (r && typeof r === 'object') {
                    const c = r.client;
                    return (c === false || c === 'false') ? false : c;
                }
                // Ancien format (string)
                return (r === false || r === 'false') ? false : r;
            }
            const r = rules.config_client;
            return (!r || r === 'false' || r === false) ? false : r;
        },
        canViewClient() {
            return this._clientRight === 'view' || this._clientRight === 'edit';
        },
        canEditClient() {
            return this._clientRight === 'edit';
        },
        canPersonal() {
            if (this._isAdmin) return true;
            const rules = this._rules || {};
            if (rules.config_type && rules.config_type[this.type] !== undefined) {
                const r = rules.config_type[this.type];
                return r !== false && r !== 'false';
            }
            return !!(rules.config_personal);
        },
        canShared() {
            if (this._isAdmin) return true;
            const rules = this._rules || {};
            return !!(rules.config_shared);
        },
        canSetDefault() {
            if (this._isAdmin) return true;
            const rules = this._rules || {};
            return !!(rules.config_set_default);
        },
        canSaveCurrentScope() {
            if (this.scope === 'personal') return this.canPersonal;
            if (this.scope === 'shared') return this.canPersonal && this.canShared;
            return this.canEditClient;
        },
        scopeOptions() {
            const opts = [];
            if (this.canPersonal) {
                opts.push({ value: 'personal', text: 'Personnelle' });
                if (this.canShared)
                    opts.push({ value: 'shared', text: 'Partagée (lecture seule)' });
            }
            if (this.canEditClient)  opts.push({ value: 'client', text: 'Publique' });
            if (this.allowedScopes)
                return opts.filter(o => this.allowedScopes.includes(o.value));
            return opts;
        },

        // ── Liste filtrée ─────────────────────────────────────────────────────
        filteredConfigs() {
            if (!this.search) return this.configList;
            const q = this.search.toLowerCase();
            return this.configList.filter(c => c.name.toLowerCase().includes(q));
        }
    },

    watch: {
        // Si les droits changent (ex: chargement async du user), corriger le scope
        scopeOptions(opts) {
            if (opts.length && !opts.find(o => o.value === this.scope))
                this.scope = opts[0].value;
        }
    },

    mounted() {
        // Choisir un scope par défaut cohérent avec les droits
        if (!this.canPersonal && this.canEditClient)
            this.scope = 'client';
    },

    methods: {
        // ── Config par défaut ────────────────────────────────────────────────

        async toggleDefault(cfg) {
            const newValue = !cfg.is_default;
            try {
                await configService.setDefault(cfg.uid, newValue);
                // Mettre à jour localement sans recharger toute la liste
                for (const c of this.configList) {
                    this.$set(c, 'is_default', c.uid === cfg.uid ? newValue : false);
                }
                // Synchroniser le bouton de la barre si c'est la config courante
                if (this.currentUid === cfg.uid) {
                    this.currentIsDefault = newValue;
                } else if (newValue) {
                    this.currentIsDefault = false;
                }
            } catch (e) {
                console.error('toggleDefault error', e);
            }
        },

        async toggleDefaultCurrent() {
            if (!this.currentUid) return;
            const newValue = !this.currentIsDefault;
            try {
                await configService.setDefault(this.currentUid, newValue);
                this.currentIsDefault = newValue;
                // Synchroniser la liste si elle est chargée
                for (const c of this.configList) {
                    this.$set(c, 'is_default', c.uid === this.currentUid ? newValue : false);
                }
            } catch (e) {
                console.error('toggleDefaultCurrent error', e);
            }
        },

        /**
         * Méthode publique : charge automatiquement la config par défaut
         * de l'utilisateur pour ce type+context, si elle existe.
         */
        async autoLoad() {
            console.log('[autoLoad] appelé — type:', this.type, '| context:', this.context);
            try {
                const res = await configService.loadDefault(this.type, this.context);
                console.log('[autoLoad] réponse brute:', JSON.stringify(res));
                if (res.config && res.config.data) {
                    console.log('[autoLoad] config trouvée uid:', res.config.uid, '| data keys:', Object.keys(res.config.data));
                    this.name             = res.config.name    || '';
                    this.version          = res.config.version || '';
                    this.currentUid       = res.config.uid;
                    this.currentIsDefault = true;
                    this.currentIsPublic  = true;
                    this.$emit('input', { ...this.value, ...res.config.data });
                    console.log('[autoLoad] $emit input effectué');
                } else {
                    console.warn('[autoLoad] pas de config.data dans la réponse:', res);
                }
            } catch (e) {
                console.error('[autoLoad] erreur:', e);
            }
        },

        // ── Sauvegarde ──────────────────────────────────────────────────────

        async save() {
            this.saveMsg   = '';
            this.saveError = false;
            if (!this.version) this.version = '1';
            try {
                const valueToSave = this.getValueFn ? await this.getValueFn() : this.value;
                await configService.save(this.name, this.version, this.type, valueToSave, this.scope, this.context);
                this.saveMsg = 'Configuration enregistrée.';
                // Rafraîchir en arrière-plan si le modal est ouvert
                await this.fetchConfigs();
            } catch (e) {
                this.saveError = true;
                this.saveMsg   = 'Erreur : ' + (e.response && e.response.data && e.response.data.msg ? e.response.data.msg : e.message);
            }
        },

        reset() {
            this.name             = '';
            this.version          = '';
            this.scope            = this.canEditClient ? 'client' : (this.canPersonal ? 'personal' : 'client');
            this.currentUid       = null;
            this.currentIsDefault = false;
            this.currentIsPublic  = false;
        },

        // ── Modal ────────────────────────────────────────────────────────────

        async openLoadModal() {
            await this.fetchConfigs();
            this.$bvModal.show(this.modalId);
        },

        async fetchConfigs() {
            this.loadingList = true;
            try {
                const res = await configService.list(this.type, this.context);
                this.configList = res.configs || [];
            } catch (e) {
                this.configList = [];
            } finally {
                this.loadingList = false;
            }
        },

        // ── Chargement (version active) ──────────────────────────────────────

        async applyConfig(cfg) {
            try {
                const res = await configService.load(cfg.uid);
                if (res.config && res.config.data)
                    this.$emit('input', { ...this.value, ...res.config.data });
                this.name             = cfg.name;
                this.version          = cfg.version || '';
                this.scope            = cfg.client_user_uid ? (cfg.shared ? 'shared' : 'personal') : 'client';
                this.currentUid       = cfg.uid;
                this.currentIsDefault = !!cfg.is_default;
                this.currentIsPublic  = !cfg.client_user_uid;
                this.$bvModal.hide(this.modalId);
            } catch (e) {
                this.$set(this.configError, cfg.uid, 'Erreur lors du chargement.');
            }
        },

        // ── Versions ─────────────────────────────────────────────────────────

        async toggleVersions(cfg) {
            const isOpen = !!this.openVersions[cfg.uid];
            this.$set(this.openVersions, cfg.uid, !isOpen);
            if (!isOpen && !this.versionsData[cfg.uid]) {
                this.$set(this.versionsData, cfg.uid, null);
                await this._loadVersions(cfg);
            }
        },

        async _loadVersions(cfg) {
            try {
                const res = await configService.versions(cfg.uid);
                this.$set(this.versionsData, cfg.uid, res.versions || []);
            } catch (e) {
                this.$set(this.versionsData, cfg.uid, []);
            }
        },

        async applyVersion(v, cfg) {
            try {
                const res = await configService.load(v.uid);
                if (res.config && res.config.data)
                    this.$emit('input', { ...this.value, ...res.config.data });
                this.name             = cfg.name;
                this.version          = v.version;
                this.scope            = cfg.client_user_uid ? (cfg.shared ? 'shared' : 'personal') : 'client';
                this.currentUid       = v.uid;
                this.currentIsDefault = !!cfg.is_default;
                this.currentIsPublic  = !cfg.client_user_uid;
                this.$bvModal.hide(this.modalId);
            } catch (e) {
                this.$set(this.configError, cfg.uid, 'Erreur lors du chargement.');
            }
        },

        async restoreVersion(v, cfg) {
            try {
                await configService.restore(v.uid);
                await this._loadVersions(cfg);
                await this.fetchConfigs();
            } catch (e) {
                this.$set(this.configError, cfg.uid, 'Erreur lors de la restauration.');
            }
        },

        async deleteVersion(v, cfg) {
            try {
                await configService.deleteOne(v.uid);
                await this._loadVersions(cfg);
                await this.fetchConfigs();
            } catch (e) {
                this.$set(this.configError, cfg.uid, 'Erreur lors de la suppression.');
            }
        },

        // ── Renommage ────────────────────────────────────────────────────────

        startRename(cfg) {
            this.renamingUid = cfg.uid;
            this.renameValue = cfg.name;
        },

        cancelRename() {
            this.renamingUid = null;
            this.renameValue = '';
        },

        async confirmRename(cfg) {
            const newName = this.renameValue.trim();
            if (!newName || newName === cfg.name) { this.cancelRename(); return; }
            try {
                await configService.rename(cfg.uid, newName);
                // Mettre à jour le champ nom si c'est la config courante
                if (this.name === cfg.name) this.name = newName;
                this.cancelRename();
                await this.fetchConfigs();
            } catch (e) {
                this.$set(this.configError, cfg.uid,
                    'Erreur : ' + (e.response && e.response.data && e.response.data.msg ? e.response.data.msg : e.message));
                this.cancelRename();
            }
        },

        // ── Suppression ──────────────────────────────────────────────────────

        async deleteConfig(cfg) {
            const ok = await this.$bvModal.msgBoxConfirm(
                `Supprimer "${cfg.name}" et toutes ses versions ?`,
                { title: 'Confirmer la suppression', okVariant: 'danger', okTitle: 'Supprimer', cancelTitle: 'Annuler' }
            );
            if (!ok) return;
            try {
                await configService.delete(cfg.uid);
                if (this.name === cfg.name) this.reset();
                await this.fetchConfigs();
            } catch (e) {
                this.$set(this.configError, cfg.uid, 'Erreur lors de la suppression.');
            }
        },

        // ── Helpers ──────────────────────────────────────────────────────────

        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        }
    }
};
</script>
